import { useState } from "react";

import { snakeCase } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { useCustomerLtvV2 } from "@features/customer-ltv-v2/hooks";
import { EXPORT_CUSTOMER_REPORT_V2 } from "@gql/customer";
import { useNorthbeamMutation } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type ExportCustomerReportV2,
  type ExportCustomerReportV2Variables,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const DataExportModal = ({ isOpen, onClose }: Props) => {
  const { user } = useUser();
  const { addToast } = useToasts();
  const { filters } = useCustomerLtvV2();

  const [fileName, setFileName] = useState("customer_ltv_data");

  const [exportData, { loading }] = useNorthbeamMutation<
    ExportCustomerReportV2,
    ExportCustomerReportV2Variables
  >(EXPORT_CUSTOMER_REPORT_V2, { useCpuIntensive: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div className="text-lg font-semibold">
          <i
            className="fa-regular fa-sharp fa-download mr-5"
            style={{ fontSize: "1.5em" }}
          />
          Export your Customer LTV Data
        </div>
      }
    >
      <div className="mt-4">
        Your customer data export will be based on the filters that you have
        applied on the Customer LTV page.
      </div>
      <div className="mt-4">
        Your export may take up to 1 hour to complete depending on the size of
        the export.
      </div>
      <div className="mt-4">
        Exports can be found in the{" "}
        <Link to="../customers/documents" target="_blank" rel="noreferrer">
          Documents page.
        </Link>
      </div>
      <div className="mt-4">
        File name:
        <TextInput
          className="text-sm"
          value={fileName}
          onChange={(e) => {
            if (e.target.value.match(/^[A-Za-z0-9_\-]*$/)) {
              setFileName(e.target.value);
            }
          }}
        />
        {!fileName && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name is required
          </div>
        )}
        {fileName && fileName.length < 3 && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name must be at least 3 characters long
          </div>
        )}
      </div>
      <div className="flex items-center justify-end mt-8">
        <PrimaryButton
          isDisabled={loading || !fileName || fileName.length < 3}
          loading={loading}
          size="md"
          onClick={async () => {
            const companyName = snakeCase(user.companyName || "");
            const timestamp = moment().format("YYYY_MM_DD_HH_mm_ss_SSS");
            const rand = Math.floor(Math.random() * 900 + 100);
            const name = `${fileName}-${companyName}-${timestamp}-${rand}`;

            const exportVars = {
              name,
              dateRange: filters.firstAcquiredMonthRange,
              breakdown: filters.breakdown?.key ?? "",
              rollupMode: filters.rollupMode,
              sumMode: filters.sumMode,
              metric: filters.metric,
              searchOperator: filters.search.operator,
              searchQuery: filters.search.query,
            };

            await exportData({
              variables: exportVars,
            });

            addToast(
              <div>
                <div>Your export has started!</div>
                <div className="mt-3">
                  When your export is done it will live in the Documents page
                  under the name:
                </div>
                <div className="mt-3">{name}.csv</div>
              </div>,
              { appearance: "success", autoDismiss: false },
            );

            prettyLogEvent.onSubmit({
              user,
              page: Pages.CUSTOMERS,
              additionalActionInfo: "customer-ltv-export",
              data: { ...exportVars },
            });

            onClose();
          }}
        >
          EXPORT
        </PrimaryButton>
      </div>
    </Modal>
  );
};
